'use client';
/**
 * L'INTERPRÉTATION BIOLOGIQUE — l'écran qui construit et éprouve les règles.
 *
 * ── Ce que cet écran doit rendre possible ───────────────────────────────────
 *
 * La RQ (24/08/2026) : « je veux que l'outil qui génère les règles soit
 * user-friendly. Développe les règles pour une analyse comme l'hépatite B, et
 * laisse-moi regarder et modifier l'outil pour le valider. »
 *
 * D'où trois partis pris, tous destinés à ce qu'on ÉPROUVE une règle avant de
 * la mettre en service, jamais après :
 *
 *  · L'ESSAI À BLANC est au premier plan, pas caché derrière un bouton. On
 *    saisit des résultats, on lit le commentaire qui serait suggéré. Rien n'est
 *    enregistré.
 *  · LA PREUVE PAR ÉNUMÉRATION énumère toutes les combinaisons possibles et
 *    dit si chacune tombe sur exactement une règle. C'est elle qui a trouvé,
 *    le 24/08, que deux règles de l'hépatite B se recouvraient — avant toute
 *    mise en service.
 *  · LA MISE EN SERVICE est un geste à part, motivé, et toute modification de
 *    règle retire le jeu du service. Un commentaire suggéré à la validation
 *    biologique finit sur un compte rendu.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { card, inputStyle, EmptyState, Skeleton } from '@/components/ui';
import { moteur } from './Registres';

import { Bouton } from '@/shared/Bouton';
import { fmtDate } from '@/lib/meta';
type Condition = {
  parametre: string;
  operateur: 'positif' | 'negatif' | 'inferieur' | 'superieurOuEgal';
  seuil?: number;
};
type Regle = {
  code: string; libelle: string;
  conditions: Condition[]; requis: string[];
  commentaire: string; reserve?: string;
};
type Jeu = {
  documentId: string; code: string; libelle: string;
  parametres: string[]; regles: Regle[]; note: string | null;
  actif: boolean; version: number;
  modifieLe: string | null; modifiePar: string | null;
  activeLe: string | null; activePar: string | null;
};

const OPERATEURS: Record<Condition['operateur'], string> = {
  positif: 'est positif',
  negatif: 'est négatif',
  inferieur: 'est inférieur à',
  superieurOuEgal: 'atteint ou dépasse',
};

/** Une condition, dite en français. C'est ainsi que la RQ la relit. */
function direCondition(c: Condition): string {
  const suite = OPERATEURS[c.operateur] || c.operateur;
  return c.seuil !== undefined && c.seuil !== null
    ? `${c.parametre} ${suite} ${String(c.seuil).replace('.', ',')}`
    : `${c.parametre} ${suite}`;
}

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

export function OngletInterpretations({ estRq }: { estRq: boolean }) {
  const [jeux, setJeux] = useState<Jeu[] | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => {
    /* Sans corps, `moteur` fait un GET — c'est son contrat. */
    void moteur('interpretations').then((r) => {
      if (!r.ok) { setErreur(r.message); setJeux([]); return; }
      setJeux(((r.data as { jeux?: Jeu[] }).jeux) || []);
    }).catch(() => { setErreur('Le serveur n’a pas répondu.'); setJeux([]); });
  }, []);
  useEffect(charger, [charger]);

  if (jeux === null) return <Skeleton rows={4} />;
  if (erreur) return <EmptyState titre="Lecture impossible" indice={erreur} />;
  if (!jeux.length) {
    return (
      <EmptyState
        titre="Aucun jeu d’interprétation"
        indice="Un jeu croise plusieurs paramètres et suggère un commentaire au biologiste. Il porte des PARAMÈTRES et non un examen : c’est ce qui permet à l’hépatite B, éclatée en sept examens, de montrer la même interprétation sur chacun." />
    );
  }

  const jeuOuvert = jeux.find((j) => j.code === ouvert) || null;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '86ch' }}>
        Un jeu croise les résultats de plusieurs paramètres et propose un commentaire au biologiste
        au moment de la validation. Il porte des <strong>paramètres</strong>, jamais un examen :
        un examen affiche les jeux dont il porte au moins un paramètre.
      </div>

      {jeux.map((j) => (
        <div key={j.code} style={{ ...card, padding: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>{j.code}</span>
            <strong style={{ fontSize: 13.5 }}>{j.libelle}</strong>
            <span style={{
              fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
              background: j.actif ? 'var(--green-50)' : 'var(--gray-100)',
              color: j.actif ? 'var(--green-700)' : 'var(--text-muted)',
            }}>{j.actif ? 'en service' : 'hors service'}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              v{j.version} · {j.regles.length} règle{j.regles.length > 1 ? 's' : ''} · {j.parametres.join(', ')}
            </span>
            <span style={{ flex: 1 }} />
            <Bouton taille="sm" onClick={() => setOuvert(ouvert === j.code ? null : j.code)}>
              {ouvert === j.code ? 'Replier' : 'Ouvrir'}
            </Bouton>
          </div>
          {ouvert === j.code && <DetailJeu jeu={j} estRq={estRq} onEcrit={charger} />}
        </div>
      ))}
      {jeuOuvert === null && null}
    </div>
  );
}

function DetailJeu({ jeu, estRq, onEcrit }: { jeu: Jeu; estRq: boolean; onEcrit: () => void }) {
  /* Le motif se saisit ICI et non dans une boîte de confirmation : `useConfirmation`
     ne rend qu'un oui/non, et le motif part au journal — il doit être écrit,
     pas coché. Le bouton reste inerte tant qu'il est trop court, avec la
     raison affichée à côté : un bouton grisé sans phrase se lit comme une
     panne (défaut rapporté par la RQ le 19/08). */
  const [motif, setMotif] = useState('');
  const [essai, setEssai] = useState<Record<string, string>>({});
  const [verdict, setVerdict] = useState<Record<string, unknown> | null>(null);
  const [couverture, setCouverture] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  /* La valeur saisie part telle quelle : le moteur sait lire « Positif »,
     « NEG », « 12,5 ». Traduire ici ferait diverger l'essai de l'exécution. */
  const lancerEssai = () => {
    setBusy(true); setMsg('');
    const resultats: Record<string, string> = {};
    for (const [k, v] of Object.entries(essai)) if (v.trim() !== '') resultats[k] = v.trim();
    void moteur('interpretations/essayer', { regles: jeu.regles, resultats })
      .then((r) => { setBusy(false); if (!r.ok) { setMsg(r.message); return; } setVerdict(r.data as Record<string, unknown>); })
      .catch(() => { setBusy(false); setMsg('Le serveur n’a pas répondu — rien n’a été enregistré.'); });
  };

  const lancerCouverture = () => {
    setBusy(true); setMsg('');
    void moteur(`interpretations/${jeu.code}/couverture`, { regles: jeu.regles })
      .then((r) => { setBusy(false); if (!r.ok) { setMsg(r.message); return; } setCouverture(r.data as Record<string, unknown>); })
      .catch(() => { setBusy(false); setMsg('Le serveur n’a pas répondu.'); });
  };

  const basculer = async () => {
    const versService = !jeu.actif;
    setBusy(true); setMsg('');
    /* La mise en service porte la version RELUE (14/10/2026) : des règles modifiées depuis sont refusées. */
    const r = await moteur(`interpretations/${jeu.code}/${versService ? 'activer' : 'retirer'}`, versService ? { motif: motif.trim(), version: jeu.version } : { motif: motif.trim() });
    setBusy(false);
    if (!r.ok) { setMsg(r.message); return; }
    setMotif('');
    onEcrit();
  };

  const v = verdict?.verdict as { etat?: string; regle?: Regle; manquants?: string[]; manquantsFacultatifs?: string[]; regles?: Regle[] } | null | undefined;
  const cov = couverture as { complet?: boolean; total?: number; couvertes?: number; trous?: unknown[]; doublons?: { regles: string[] }[] } | null;

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 14 }}>

      {jeu.note && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, background: 'var(--surface-sunken)', padding: '9px 11px', borderRadius: 'var(--radius-md)' }}>
          {jeu.note}
        </div>
      )}

      {/* ── LES RÈGLES, dites en français ─────────────────────────────────── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 }}>
          Règles — dans l’ordre de lecture
        </div>
        <div style={{ display: 'grid', gap: 7 }}>
          {jeu.regles.map((r) => (
            <div key={r.code} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '9px 11px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ ...mono, color: 'var(--text-muted)' }}>{r.code}</span>
                <strong style={{ fontSize: 13 }}>{r.libelle}</strong>
              </div>
              <div style={{ fontSize: 12.5, marginTop: 5, color: 'var(--text-body)' }}>
                Si {r.conditions.map((c, i) => (
                  <span key={i}>
                    {i > 0 && <span style={{ color: 'var(--text-muted)' }}> et </span>}
                    <span style={{
                      background: r.requis.includes(c.parametre) ? 'var(--cyan-50)' : 'var(--gray-50)',
                      borderRadius: 'var(--radius-xs)', padding: '1px 6px',
                    }}>{direCondition(c)}</span>
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.55 }}>
                <span style={{ color: 'var(--text-muted)' }}>→ </span>{r.commentaire}
              </div>
              {r.reserve && (
                <div style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.5, color: 'var(--status-warning)' }}>
                  Réserve — {r.reserve}
                </div>
              )}
              {/* Un paramètre en surbrillance claire est REQUIS ; en gris, il
                  affine sans conditionner. La distinction se voit, elle ne se
                  devine pas. */}
              <div style={{ fontSize: 12.5, marginTop: 5, color: 'var(--text-faint)' }}>
                requis : {r.requis.join(', ') || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── L'ESSAI À BLANC ───────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 11 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 7 }}>
          Essayer — rien n’est enregistré
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {jeu.parametres.map((p) => (
            <label key={p} style={{ fontSize: 12.5 }}>
              <span style={{ ...mono, display: 'block', color: 'var(--text-muted)', marginBottom: 3 }}>{p}</span>
              <input value={essai[p] || ''} placeholder="positif / négatif / valeur"
                onChange={(e) => setEssai({ ...essai, [p]: e.currentTarget.value })}
                style={{ ...inputStyle, width: 190 }} />
            </label>
          ))}
          <Bouton variante="primaire" disabled={busy} onClick={lancerEssai}>Essayer</Bouton>
          <Bouton taille="sm" disabled={busy} onClick={lancerCouverture}>
            Éprouver toutes les combinaisons
          </Bouton>
        </div>

        {v && (
          <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}>
            {v.etat === 'conclu' && v.regle && (
              <>
                <div><strong>{v.regle.libelle}</strong> <span style={{ ...mono, color: 'var(--text-muted)' }}>{v.regle.code}</span></div>
                <div style={{ marginTop: 4 }}>{v.regle.commentaire}</div>
                {v.regle.reserve && <div style={{ marginTop: 4, color: 'var(--status-warning)' }}>Réserve — {v.regle.reserve}</div>}
                {/* ⚠️ CETTE PHRASE SE LISAIT COMME LA SUITE DE LA RÉSERVE.
                    RQ, 25/08/2026, devant « Aurait pu affiner : HBV_ACHBS »
                    placé sous une réserve qui parle des IgM anti-HBc : « tu veux
                    dire anti-HBs de type IgM ? » Non — et rien à l'écran ne
                    permettait de le savoir. Deux énoncés sans rapport, collés
                    l'un à l'autre, et un CODE nu là où il fallait une phrase.

                    Elle est désormais autonome : elle dit de quoi elle parle
                    (un paramètre NON RENDU sur cet essai), et pourquoi cela
                    compte (facultatif pour cette règle — elle a conclu sans). */}
                {!!(v.manquantsFacultatifs || []).length && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border-default)', color: 'var(--text-muted)' }}>
                    <strong>Sans rapport avec la réserve ci-dessus :</strong>{' '}
                    {(v.manquantsFacultatifs || []).length > 1 ? 'ces paramètres n’ont pas' : 'ce paramètre n’a pas'}{' '}
                    été rendu sur cet essai —{' '}
                    <span style={{ ...mono, color: 'var(--text-strong)' }}>
                      {(v.manquantsFacultatifs || []).join(', ')}
                    </span>. {(v.manquantsFacultatifs || []).length > 1 ? 'Ils sont' : 'Il est'}{' '}
                    <strong>facultatif{(v.manquantsFacultatifs || []).length > 1 ? 's' : ''}</strong> pour cette
                    règle, qui a donc conclu sans {(v.manquantsFacultatifs || []).length > 1 ? 'eux' : 'lui'} ;
                    {(v.manquantsFacultatifs || []).length > 1 ? ' les' : ' le'} rendre aurait affiné la lecture.
                  </div>
                )}
              </>
            )}
            {v.etat === 'incomplet' && (
              <div style={{ color: 'var(--status-warning)' }}>
                Rien n’est suggéré — il manque {(v.manquants || []).join(', ')}.
                Un résultat illisible compte comme absent : ne pas savoir lire n’est pas lire « négatif ».
              </div>
            )}
            {v.etat === 'ambigu' && (
              <div style={{ color: 'var(--red-600)' }}>
                Deux règles concluent ensemble ({(v.regles || []).map((r) => r.code).join(' + ')}) —
                c’est une faute de spécification, pas un cas clinique. Le moteur refuse de choisir.
              </div>
            )}
            {v.etat === 'aucune' && (
              <div style={{ color: 'var(--text-muted)' }}>Aucune règle ne correspond à cette combinaison.</div>
            )}
          </div>
        )}

        {cov && (
          <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}>
            <strong>{cov.couvertes} combinaison{(cov.couvertes || 0) > 1 ? 's' : ''} sur {cov.total}</strong>
            {cov.complet
              ? <span style={{ color: 'var(--green-700)' }}> — aucun trou, aucun doublon.</span>
              : (
                <span style={{ color: 'var(--red-600)' }}>
                  {' '}— {(cov.trous || []).length} sans règle, {(cov.doublons || []).length} à deux règles
                  {(cov.doublons || []).length > 0 && ` (${(cov.doublons || []).map((d) => d.regles.join(' + ')).join(' ; ')})`}.
                </span>
              )}
          </div>
        )}
        {msg && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--red-600)' }}>{msg}</div>}
      </div>

      {estRq && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={motif} onChange={(e) => setMotif(e.currentTarget.value)}
            placeholder={jeu.actif ? 'Pourquoi ce jeu quitte le service' : 'Pourquoi ce jeu est prêt — cette phrase reste au journal'}
            style={{ ...inputStyle, minWidth: 320, flex: 1 }} />
          <Bouton variante={jeu.actif ? 'contour' : 'primaire'}
            disabled={busy || motif.trim().length < 5} onClick={basculer}>
            {jeu.actif ? 'Retirer du service' : 'Mettre en service'}
          </Bouton>
          {motif.trim().length < 5 && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Un motif d’au moins cinq caractères est requis.
            </span>
          )}
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flexBasis: '100%' }}>
            {jeu.actif
              ? `En service depuis le ${fmtDate(jeu.activeLe || '')}. Toute modification de règle le retire du service.`
              : 'Hors service : aucun commentaire n’est suggéré au biologiste tant qu’il n’est pas mis en service.'}
          </span>
        </div>
      )}
    </div>
  );
}
